import { CONFIG, PRESETS, ZONES, LINE_DEFS, HOOP } from '../constants.js'
import { resolveLineExclusions } from './lineToggle.js'
import { pickZonePalette } from '../tiles/zonePalette.js'
import { hoopOffsetRange, clampHoopOffset } from '../hoops/hoopPosition.js'

// ============================================================================
//  configCodec — snapshot konfiguracji boiska (format v1) ↔ base64url.
//  Jeden format obsługuje zapisy w localStorage i link #cfg= dla wyceny.
//  Czysty moduł (bez DOM/THREE/import.meta) — testowalny pod Node; katalogi
//  przekazywane argumentami, mikro-resolvery powielone jak w quotePayload.js.
// ============================================================================

const B64_RE = /^[A-Za-z0-9_-]+$/

function pickModel(catalog, modelId) {
  return catalog.models.find((m) => m.id === modelId) || catalog.models[0]
}

// Snapshot v1: tylko pola potrzebne do odtworzenia. lineW/lineL są pochodne
// (wylicza applyCustom/applyPreset), view to stan sceny — świadomie pomijane.
export function snapshotConfig(state) {
  return {
    v: 1,
    model: state.model,
    hoop: state.hoop,
    hoopOffsetTiles: state.hoopOffsetTiles ?? 0,
    widthTiles: state.widthTiles,
    lengthTiles: state.lengthTiles,
    runoffTiles: state.runoffTiles,
    // lineSpec zapisujemy na zapas (wymóg formatu ze specyfikacji), ale ŻADNA
    // ścieżka odtwarzania go dziś nie czyta — obie liczą go od nowa przez
    // applyPreset/applyCustom. Trzymane pod przyszłe warianty linii.
    lineSpec: state.lineSpec,
    preset: state.preset,
    colors: { ...state.colors },
    lines: { ...state.lines },
  }
}

// JSON → UTF-8 → base64url (bez + / =). btoa/atob są globalne w przeglądarce
// i w Node 20, TextEncoder/TextDecoder również.
export function encodeConfig(snapshot) {
  const bytes = new TextEncoder().encode(JSON.stringify(snapshot))
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Liczba całkowita przycięta do zakresu; wszystko inne → default zakresu.
const clampInt = (x, { min, max, default: dflt }) =>
  Number.isInteger(x) ? Math.min(max, Math.max(min, x)) : dflt

// Walidacja surowego obiektu względem katalogów — jedna definicja „poprawnego
// snapshotu" dla OBU ścieżek odtwarzania (link #cfg= i zapis z localStorage).
// Zapis mógł powstać przed zmianą tiles.json, więc model/kolory zawsze schodzą
// do wartości istniejących w katalogu, a wymiary są przycinane do zakresu.
// Śmieci / zła wersja formatu → null.
export function sanitizeSnapshot(raw, { catalog, hoopCatalog }) {
  if (!raw || raw.v !== 1) return null

  const model = pickModel(catalog, raw.model)
  const colors = {}
  // Kolor walidowany względem palety STREFY (strefy linii mają własny zakres
  // 'court-lines') — spójnie z resolve/reconcileColors w main.
  ZONES.forEach(({ key }) => {
    const id = raw.colors?.[key]
    const palette = pickZonePalette(catalog, model.id, key)
    colors[key] = palette.some((p) => p.id === id) ? id : model.defaultColors[key]
  })
  // Biała lista kluczy linii + rozstrzygnięcie wykluczeń (link z oboma
  // wariantami RW → zostaje półkole); stary link bez `ftHalf` → false (koło
  // z zapisu zostaje, nic nie dopisujemy).
  const lines = resolveLineExclusions(
    Object.fromEntries(LINE_DEFS.map(({ key }) => [key, raw.lines?.[key] === true])),
  )

  let hoop = null
  if (hoopCatalog) {
    hoop = hoopCatalog.hoops.some((h) => h.id === raw.hoop) ? raw.hoop : hoopCatalog.default
  }

  // Pozycja kosza (suwak): clamp do zakresu policzonego z ODTWORZONYCH wymiarów
  // i wysięgu kosza — ta sama reguła co przy suwaku (hoopOffsetRange); śmieci
  // i stare zapisy bez pola → 0 (pozycja standardowa).
  const lengthTiles = clampInt(raw.lengthTiles, CONFIG.LENGTH)
  const hoopDef = hoop ? hoopCatalog.hoops.find((h) => h.id === hoop) : null
  const hoopOffsetTiles = clampHoopOffset(raw.hoopOffsetTiles, hoopOffsetRange({
    lengthTiles,
    tileM: model.size.width / 1000,
    rimFromBaseline: hoopDef?.rimFromBaseline ?? HOOP.RIM_FROM_BASELINE,
  }))

  // Wybieg: minimum jest globalne (0 — dozwolone w każdym presecie), a wartość
  // domyślna (dla śmieci/braku pola) per preset — „Mały" startuje bez wybiegu.
  const preset = raw.preset === 'custom' || PRESETS.some((p) => p.id === raw.preset) ? raw.preset : 'custom'
  const presetDef = PRESETS.find((p) => p.id === preset)
  const runoffTiles = clampInt(raw.runoffTiles, {
    max: CONFIG.RUNOFF.max,
    min: CONFIG.RUNOFF.min,
    default: presetDef?.runoffDefault ?? CONFIG.RUNOFF.default,
  })

  return {
    v: 1,
    model: model.id,
    hoop,
    hoopOffsetTiles,
    widthTiles: clampInt(raw.widthTiles, CONFIG.WIDTH),
    lengthTiles,
    runoffTiles,
    lineSpec: raw.lineSpec === 'fiba' ? 'fiba' : 'compact',
    preset,
    colors,
    lines,
  }
}

// Dekodowanie + walidacja względem katalogów. Dowolne śmieci → null
// (aplikacja startuje wtedy normalnie, bez komunikatów).
export function decodeConfig(b64, { catalog, hoopCatalog }) {
  try {
    if (typeof b64 !== 'string' || !B64_RE.test(b64)) return null
    const bin = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
    const raw = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))))
    return sanitizeSnapshot(raw, { catalog, hoopCatalog })
  } catch {
    return null
  }
}

// Gotowy link do wklejenia w payload wyceny (origin z location wywołującego).
export function buildViewUrl(state, origin) {
  return `${origin}/#cfg=${encodeConfig(snapshotConfig(state))}`
}
